import type { Tutorial } from '../../shared/types.js';

export const organizationTutorial: Tutorial = {
  name: 'organization',
  title: 'Tour Configure',
  description:
    'A guided walk through Configure: people (members, teams, roles), environments and agents, and organization settings.',
  category: 'organization',
  docPages: ['administration/organization-teams'],
  agenda: [
    'Members, teams, roles, and permissions',
    'Environments and agents',
    'Organization settings',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Configure',
      body: 'Configure is where you decide who can reach this organization, how they are grouped, what they are allowed to do, and what agents run on. **Everything here is scoped to the active organization.** Press Escape or Back at any time to return to your sessions. Let us take a look.',
      path: '/configure/people/members',
    },
    {
      id: 'members',
      kind: 'info',
      title: 'Members',
      body: 'Members are the people in this organization. **Admins** can invite new people, change the role someone holds, and remove access. Every member of an organization can view the membership roster.',
      target: '[data-tutorial-id="nav-members"]',
      placement: 'right',
    },
    {
      id: 'teams',
      kind: 'info',
      title: 'Teams',
      body: 'Group members into teams to organize ownership and surface team-level activity. Teams can also hold their own **model-provider keys**, so a team can share credentials without exposing them org-wide.',
      target: '[data-tutorial-id="nav-teams"]',
      placement: 'right',
    },
    {
      id: 'roles',
      kind: 'info',
      title: 'Roles and permissions',
      body: 'Roles decide what each member can do. Start from the built-in **owner, admin, and member** roles, or create custom roles with bespoke permission sets when you need finer-grained access.',
      target: '[data-tutorial-id="nav-roles"]',
      placement: 'right',
    },
    {
      id: 'blueprints',
      kind: 'info',
      title: 'Environments',
      body: 'Dockerfile-based templates (blueprints) that define the OS, packages, dependencies, and any setup commands for agent devboxes. Build one once and every agent launched from it starts in an identical, pre-configured environment.',
      target: '[data-tutorial-id="nav-blueprints"]',
      placement: 'right',
    },
    {
      id: 'agent-models',
      kind: 'info',
      title: 'Agents',
      body: 'Personas and the policy layer for which models, providers, and endpoint routes are enabled for this organization. Set Launch dialog defaults and toggle off anything you do not want agents reaching for.',
      target: '[data-tutorial-id="nav-agent-models"]',
      placement: 'right',
    },
    {
      id: 'settings',
      kind: 'info',
      title: 'Organization',
      body: 'Rename the organization, change its **URL slug**, and manage **service accounts** for CI and automation. These details surface across the platform in invites, audit logs, and the org switcher.',
      target: '[data-tutorial-id="nav-org-settings"]',
      placement: 'right',
    },
    {
      id: 'done',
      kind: 'info',
      title: 'That is the tour',
      body: 'You now know your way around Configure: people, environments and agents, and organization settings. Credentials and integrations live in **Connect**, alongside MCP and sharing. Want to wire up a service next?',
    },
  ],
  related: [
    { tutorialName: 'connect-github', label: 'Connect GitHub' },
    { tutorialName: 'connect-slack', label: 'Connect Slack' },
  ],
};
